import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Button, TextField, Grid, Typography, Box,
  Alert, CircularProgress
} from '@mui/material';
import Api from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

// Parse "YYYY-MM-DD HH:MM:SS" → { date: "YYYY-MM-DD", time: "HH:MM" }
const parseTransactionTime = (timeStr) => {
  if (!timeStr) return { date: '', time: '' };
  const [date, timePart] = timeStr.split(' ');
  const time = timePart ? timePart.slice(0, 5) : '';
  return { date, time };
};

const todayString = () => new Date().toISOString().slice(0, 10);

const EditManualPaymentModal = ({ open, onClose, transaction, onSuccess }) => {
  const { palette, Colors } = useAppTheme();

  const [date,      setDate]      = useState('');
  const [time,      setTime]      = useState('');
  const [reference, setReference] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    if (transaction) {
      const { date: d, time: t } = parseTransactionTime(transaction.transaction_time);
      setDate(d);
      setTime(t);
      setReference(transaction.transaction_id);
      setError('');
    }
  }, [transaction]);

  const handleSave = async () => {
    setError('');

    if (!reference.trim()) {
      setError('Transaction reference cannot be empty.');
      return;
    }

    if (date) {
      const chosen = new Date(`${date}T${time || '00:00'}:00`);
      if (chosen > new Date()) {
        setError('Payment date/time cannot be in the future.');
        return;
      }
    }

    setLoading(true);
    try {
      const paymentDatetime = date ? `${date}T${time || '00:00'}:00` : null;
      const newRef = reference.trim() !== transaction.transaction_id ? reference.trim() : null;

      const result = await Api.editManualPayment(
        transaction.transaction_id,
        paymentDatetime,
        newRef
      );

      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update payment.');
    } finally {
      setLoading(false);
    }
  };

  const dialogPaper = { sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } };
  const primaryBtn  = { sx: { backgroundColor: Colors.primary.main, '&:hover': { backgroundColor: Colors.primary.dark } } };
  const cancelBtn   = { sx: { color: palette.text.secondary } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={dialogPaper}>
      <DialogTitle sx={{ color: palette.text.primary }}>Edit Manual Payment</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: palette.text.secondary, mb: 2 }}>
          Only the payment date/time and transaction reference can be edited.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Reference */}
          <Grid item xs={12}>
            <TextField
              label="Transaction Reference"
              fullWidth
              value={reference}
              onChange={e => setReference(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Date + Time */}
          <Grid item xs={7}>
            <TextField
              label="Payment Date"
              type="date"
              fullWidth
              value={date}
              onChange={e => setDate(e.target.value)}
              inputProps={{ max: todayString() }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="Time"
              type="time"
              fullWidth
              value={time}
              onChange={e => setTime(e.target.value)}
              inputProps={{ step: 60 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} {...cancelBtn}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : null} {...primaryBtn}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditManualPaymentModal;